import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { logger } from '@/config/logger';
import { PricingModel, useCreateCheckout } from '@/modules/api/codegen';
import { actions as messagesActions } from '@/modules/messages';

import type { IframeMessagePayload } from './useReceiveIframeMessages';

interface UpgradeButtonClickPayload {
    plan: 'workspace';
    mode: 'perUser' | 'perWorkspace';
    currency: 'eur' | 'usd';
    coupon?: string;
    clientReferenceId?: string;
}

const EVENT_TYPE = 'upgrade-button-click';

export const isUpgradeButtonClickPayload = (payload: Record<string, any>): payload is UpgradeButtonClickPayload => {
    return typeof payload === 'object' && payload !== null && 'plan' in payload && 'mode' in payload && 'currency' in payload;
};

export const useCheckout = () => {
    const dispatch = useDispatch();
    const { mutateAsync, isPending } = useCreateCheckout();

    const checkout = useCallback(
        async (eventData: IframeMessagePayload) => {
            if (eventData.eventType === EVENT_TYPE && eventData.payload && isUpgradeButtonClickPayload(eventData.payload)) {
                try {
                    const res = await mutateAsync({
                        data: {
                            currency: eventData.payload.currency,
                            pricingModel: eventData.payload.mode === 'perUser' ? PricingModel.perUser : PricingModel.workspace,
                            couponId: eventData.payload.coupon,
                            clientReferenceId: eventData.payload.clientReferenceId,
                        },
                    });

                    if (res.data.url) {
                        window.location.href = new URL(res.data.url).href;
                    } else {
                        throw new Error('No URL returned from the server');
                    }
                } catch (error) {
                    dispatch(
                        messagesActions.displayErrorMessage({
                            message: { id: 'error.saas-upgrade.saasCheckout' },
                            options: { duration: 10 },
                        }),
                    );

                    logger.error(error);
                }
            }
        },
        [dispatch, mutateAsync],
    );

    return { checkout, isPending };
};
